import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { hasPermission } from '../utils/permissions';

const OpenOrdersCountContext = createContext(null);

export const OpenOrdersCountProvider = ({ children }) => {
  const { user, loading: authLoading, userRole, customPermissions } = useAuth();
  const [openOrdersCount, setOpenOrdersCount] = useState(0);
  const [openSpecialRequestsCount, setOpenSpecialRequestsCount] = useState(0);
  const canManageSpecialRequests = hasPermission(userRole, 'manage_orders', customPermissions);

  const refreshOpenOrdersCount = useCallback(async () => {
    if (!user) {
      setOpenOrdersCount(0);
      setOpenSpecialRequestsCount(0);
      return;
    }
    try {
      const res = await axios.get('/api/orders/open-count');
      setOpenOrdersCount(Number(res.data?.count) || 0);
    } catch {
      setOpenOrdersCount(0);
    }

    if (canManageSpecialRequests) {
      try {
        const res = await axios.get('/api/special-requests/open-count');
        setOpenSpecialRequestsCount(Number(res.data?.count) || 0);
      } catch {
        setOpenSpecialRequestsCount(0);
      }
    } else {
      setOpenSpecialRequestsCount(0);
    }
  }, [user, canManageSpecialRequests]);

  useEffect(() => {
    if (authLoading) return;
    refreshOpenOrdersCount();
  }, [authLoading, refreshOpenOrdersCount]);

  useEffect(() => {
    const onFocus = () => {
      if (user) refreshOpenOrdersCount();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user, refreshOpenOrdersCount]);

  const openOrdersMenuCount = openOrdersCount + openSpecialRequestsCount;

  return (
    <OpenOrdersCountContext.Provider value={{
      openOrdersCount,
      openSpecialRequestsCount,
      openOrdersMenuCount,
      refreshOpenOrdersCount,
    }}
    >
      {children}
    </OpenOrdersCountContext.Provider>
  );
};

export const useOpenOrdersCount = () => {
  const ctx = useContext(OpenOrdersCountContext);
  if (ctx == null) {
    throw new Error('useOpenOrdersCount must be used within OpenOrdersCountProvider');
  }
  return ctx;
};
